import {WedModel} from "back/edit/wed/wedCore";
import {IWedEdBar, IWedEditor, IWedEditorMain, OWedEditorMainConfig, OWedManagerConfig} from "back/edit/wed/wedEditor";
import {OFindWedletOtlOptions, OtlWedlet} from "back/edit/wed/wedlets/outline/outline";
import {IS_OtlTree, OtlEditor} from "back/edit/wed/wedlets/outline/outlineTags";
import {IElementWedlet, IS_EltWedlet, isTargetableWedlet, ITargetableWedlet, OFindWedletOptions, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {Action, IActionToggle} from "lib/commons/actions";
import {JLastDatas} from "lib/commons/lastDatas";
import {REG} from 'lib/commons/registry';
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IXAddr} from "lib/commons/xml/xAddr";

/** Config de l'editor dédié à l'affichage de l'outline. */
export interface OWedEditorOutlineConfig extends OWedEditorMainConfig {
}

/** Enrichissements de l'éditor principal pour gérer l'outline. */
export interface IWedEditorOutlineBar extends IWedEditorMain {
	outlineBar?: OtlBar
	outlineWedModel?: WedModel

	showOutline(show: boolean): void
}

export function AgOutlineBarEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorOutlineBar;

	proto.showOutline = function (this: IWedEditorOutlineBar, show: boolean) {
		if (show) {
			if (!this.outlineWedModel) return;
			if (!this.outlineBar) {
				const init = Object.create(this.config) as OWedEditorOutlineConfig;
				init.lastDatasKey = "otl";
				init.lastDatas = this.config.lastDatas && this.config.lastDatas[init.lastDatasKey];
				this.outlineBar = new OtlBar().initialize(init).initOutline(this);
				//let scrollCtn = this.scrollContainer;
				//if (scrollCtn) scrollCtn.addEventListener("scroll", () => {this.outlineBar.refreshHighlight(false)}, {passive: true});
				this.outlineBar.refreshHighlightCb = () => {this.outlineBar.refreshHighlight(true)};
				this.wedMgr.listeners.on("getFocus", this.outlineBar.refreshHighlightCb);
			} else this.outlineBar.hidden = false;
			this.barLayout.showBar(this.outlineBar);
		} else if (this.outlineBar) {
			if (DOM.setHidden(this.outlineBar, true)) this.barLayout.onHideBar(this.outlineBar);
		}
	};

	if (!proto.initLastDatasHooks) proto.initLastDatasHooks = [];
	proto.initLastDatasHooks.push(function (this: IWedEditorOutlineBar, lastDatas: JLastDatas) {
		if (lastDatas.otl) {
			if (this.wedMgr.rootWedlet) {
				this.showOutline(true);
			} else {
				this.wedMgr.listeners.once("redrawAtEnd", () => {this.showOutline(true)});
			}
		}
	});

	if (!proto.buildLastDatasHooks) proto.buildLastDatasHooks = [];
	proto.buildLastDatasHooks.push(function (this: IWedEditorOutlineBar, lastDatas: JLastDatas) {
		if (this.outlineBar && !this.outlineBar.hidden) this.outlineBar.buildLastDatas(lastDatas);
	});

	return cls;
}

class OtlBar extends OtlEditor implements CustomElement, IWedEdBar {
	mainEditor: IWedEditorOutlineBar;

	constructor() {
		super();
		this.id = "outlineBar";
		this.setAttribute("label", "Niveaux de titres");
	}

	connectedCallback() {
		super.connectedCallback();
		REG.findReg(this).installSkin('wedEditor/otl-bar', this);
	}

	protected setTreeRoot(treeRoot: ShadowRoot) {
		super.setTreeRoot(treeRoot);
		//this.treeRoot.addEventListener("pointerDown", this.onPointerDown);
		this.treeRoot.addEventListener("click", this.onClick);
	}

	initOutline(mainEditor: IWedEditorOutlineBar): this {
		this.mainEditor = mainEditor;
		const mainWedMgr = this.mainEditor.wedMgr;
		this.wedModel = this.mainEditor.outlineWedModel || mainWedMgr.wedModel;
		mainWedMgr.listeners.on("killEditor", () => {if (this.idleCbPending) clearTimeout(this.idleCbPending)});
		mainWedMgr.hookFct("clearEditor", () => {this.wedMgr.clearEditor()});
		mainWedMgr.hookFct("detachDocHolder", () => {this.wedMgr.detachDocHolder()});
		mainWedMgr.listeners.on("readOnlyChangeAfter", (mainWedMgr, cause, readOnly, more) => {
			this.wedMgr.setReadOnly(cause, readOnly, more);
		});

		const redrawOutline = () => {
			//init de l'editor de l'outline au redraw de l'editor principal.
			this.wedMgr.detachDocHolder(); //en cas d'un redraw suite à un echec / coupure réseau.
			const wedMgrConfig = {} as OWedManagerConfig;
			wedMgrConfig.xaRoot = mainWedMgr.config.xaRoot;
			wedMgrConfig.readOnlyCauses = Array.from(mainWedMgr.readOnlyCauses);
			wedMgrConfig.dataTransferAnalyzer = mainWedMgr.config.dataTransferAnalyzer;
			wedMgrConfig.noAnnots = true;
			this.initFromDocHolder(mainWedMgr.docHolderAsync.cloneDocHolder(), wedMgrConfig).then(() => this.onOtlEntryChange());
		};
		mainWedMgr.listeners.on("redrawAtEnd", redrawOutline); //futures (re)contrcuxtion
		if (mainWedMgr.drawn) redrawOutline(); //construction si le mainWedMgr est déjà actif.
		return this;
	}

	onOtlRowUpdated() {
		this.onOtlEntryChange();
		this.refreshHighlight(true);
	}

	onOtlEntryChange() {
	}

	requestIdleCallback = requestIdleCallback.bind(this);
	idleCbPending: number | null;

	highlightFromXa(xa: IXAddr): boolean {
		//console.log("highlightFromXa:::", xa);
		const root = this.wedMgr.rootWedlet;
		if (root) return this.highlightOtlEntry(OtlEditor.findOtlTree(WEDLET.findWedlet(root, xa, WEDLETFIND_inHier) as OtlWedlet));
		return false;
	}

	refreshHighlight(force?: boolean) {
		if (!this.wedMgr.rootWedlet) return; //pas de wedlet outline trouvé, exit.
		if (this.idleCbPending != null) return; //déjà planifié.
		//TODO trouver une solution pour détcter la fin du scroll smooth pour ne pas l'interrompre ?
		this.idleCbPending = setTimeout(this.requestIdleCallback, 800);
		//if (!this.idleCbPending) this.idleCbPending = window.requestIdleCallback(this.requestIdleCallback);
	}

	refreshHighlightCb: () => void;

	protected async onClick(this: ShadowRoot, ev: MouseEvent) {
		//console.log("onPointerDown otlEditor::::", this, ev.target, ev);
		const otlTree = DOM.findParent(ev.target as Node, this, IS_OtlTree);
		if (otlTree) {
			const otlEditor = this.host.parentElement as OtlBar;
			otlEditor.highlightOtlEntry(otlTree);//Pour court-circuiter le timer (hack pb interruption smooth scrolling)
			const opts: OFindWedletOptions = {forceFetch: true, lastAncestorIfNone: true};
			let wedletOwner = WEDLET.findWedlet(otlEditor.mainEditor.wedMgr.rootWedlet, otlTree.wedlet.wedAnchor, opts);
			if (opts.forceFetchPromises) wedletOwner = WEDLET.getFirstWedlet(await Promise.all(opts.forceFetchPromises));
			while (wedletOwner && !isTargetableWedlet(wedletOwner)) wedletOwner = wedletOwner.wedParent;
			if (wedletOwner) {
				//Réduit bug chrome scroll smooth si des BoxOffView on été résolus à la volée changeant les offset
				//console.log("opts.contentUpdated:::::", opts.contentUpdated);
				setTimeout(() => {if (otlTree.isConnected) (wedletOwner as ITargetableWedlet).highlightFromLink('start')}, 100);
			}
			ev.preventDefault();
		}
	}
}

REG.reg.registerSkin("wedEditor/otl-bar", 1, /*language=CSS*/ `
	otl-bar {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}
`);

customElements.define("otl-bar", OtlBar);

function requestIdleCallback(this: OtlBar) {
	// if (deadline.timeRemaining() < 3) {
	// 	this.idleCbPending = window.requestIdleCallback(this.requestIdleCallback);
	// 	return;
	// }
	this.idleCbPending = null;
	let elt: Element;
	//let scrollRect = this.mainEditor.scrollContainer.getBoundingClientRect();
	const activeElt = DOMSH.findDeepActiveElement(DOMSH.findDocumentOrShadowRoot(this.mainEditor.rootNode));
	elt = activeElt;
	// if (activeElt) {
	// 	//si l'activeElt est dans le viewPort.
	// 	let activeRect = activeElt.getBoundingClientRect();
	// 	if (activeRect.bottom > scrollRect.top && activeRect.top < scrollRect.bottom) elt = activeElt;
	// 	console.log("Active elt found:::", elt);
	// }
	// if (!elt) {
	// 	let yChecks = [];
	// 	yChecks.push(scrollRect.top + 1);
	// 	if (scrollRect.height > 30) yChecks.push(scrollRect.top + 20);
	// 	if (scrollRect.height > 50) yChecks.push(scrollRect.top + 40);
	// 	if (scrollRect.height > 100) yChecks.push(scrollRect.top + 60);
	// 	elt = deepestEltFromPoints(this.mainEditor.rootNode, scrollRect.left + scrollRect.width / 2, yChecks);
	// 	console.log("Deepest elt found:::", elt);
	// }
	if (elt) {
		const eltWedlet = DOMSH.findFlatParentEltOrSelf(elt, this.mainEditor.rootNode, IS_EltWedlet) as IElementWedlet;
		if (eltWedlet) this.highlightFromXa(eltWedlet.wedlet.wedAnchor);
	}
}

// function deepestEltFromPoints(from: Element, x: number, y: number[]): Element {
// 	let rootCtx = DOMSH.findDocumentOrShadowRoot(from);
// 	let elt = eltFromPoint(rootCtx, x, y);
// 	while (elt && elt.shadowRoot) {
// 		let ch = eltFromPoint(elt.shadowRoot, x, y);
// 		if (!ch) break;
// 		elt = ch;
// 	}
// 	return elt;
// }
//
// function eltFromPoint(rootCtx: DocumentOrShadowRoot & Node, x: number, y: number[]): Element {
// 	let result: Element;
// 	let resultDeep = -1;
// 	for (let i = 0; i < y.length; i++) {
// 		let elt = rootCtx.elementFromPoint(x, y[i]);
// 		if (elt && DOM.isAncestor(rootCtx, elt)) {
// 			let eltDeep = DOM.computeDepth(elt);
// 			if (eltDeep > resultDeep) {
// 				result = elt;
// 				resultDeep = eltDeep;
// 			}
// 		}
// 	}
// 	return result;
// }

const WEDLETFIND_inHier = {lastAncestorIfNone: true, mainBranch: true} as OFindWedletOtlOptions;


export class ShowOutlineAction extends Action<IWedEditor & IWedEditorOutlineBar> implements IActionToggle<IWedEditor & IWedEditorOutlineBar> {

	constructor() {
		super("outline");
		this._label = "Niveaux de titres";
		this._icon = "/@skin@/edit/wed/outline/outlineBtn.svg";
	}

	isVisible(ctx: IWedEditor & IWedEditorOutlineBar) {
		return ctx.outlineWedModel != null;
	}

	isToggle(): this is IActionToggle<IWedEditor & IWedEditorOutlineBar> {return true}

	getDatas(api: 'toggle', ctx: IWedEditor & IWedEditorOutlineBar): boolean | null {
		return ctx.outlineBar != null && !ctx.outlineBar.hidden;
	}

	execute(ctx: IWedEditor & IWedEditorOutlineBar, ev?: Event): any | 'noPreventDefault' {
		ctx.showOutline(!ctx.outlineBar || ctx.outlineBar.hidden);
	}
}